import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import 'twin.macro';
import AppPage from 'components/page/app-page/AppPage';
import SaveStatus from 'components/save-status/SaveStatus';
import { nextStepButton } from 'components/save-status/nextStepButton';
import UndoRedo from 'components/undo-redo/UndoRedo';
import { useAutoSave } from 'hooks/useAutoSave';
import { useUndoRedo } from 'hooks/useUndoRedo';
import { usePendingSave } from 'context/PendingSaveContext';
import {
  BasedOn,
  JobPageRefusal,
  withCount,
  jobPagePath,
  jobPageRequest,
  salaryNotSavedNote,
  saveJobPage,
} from 'api/jobPageAutoSave';
import { sendKeepaliveSave } from 'utils/keepaliveSave';
import { WORK_WAS_KEPT } from 'api/waitingChanges';
import OutOfDateBox from 'components/out-of-date/OutOfDateBox';
import VacancyPreviewer from 'components/previewers/VacancyPreviewer';
import useNavigationContext from 'hooks/context/nav-context';
import useVacancyContext from 'hooks/context/vacancy-context';
import { CopyToClipboard } from 'components/copy-to-clipboard/CopyToClipboard';
import { useLazyQuery } from 'hooks/sympl-query';
import { GET_JOBTYPES_SUBJOBTYPES } from 'graphql/jobtypes/queries';
import { EXPERIENCE, JobType } from 'types/targetingTypes';
import Button from 'components/button/Button';
import InputGroup from 'components/form/input-group/InputGroup';
import PillGroup from 'components/form/pill-group/PillGroup';
import { Modal } from 'components/page/app-page/Modal';
import { Title1, Title3 } from 'components/typography/Typography';
import { Controller, useForm } from 'react-hook-form';
import Input from 'components/form/input/Input';
import useApiList from 'hooks/useApiList';
import { isTextEditorEmpty, popConfetti, stripHtml } from 'utils/baseHelpers';
import useGetStartedContext from 'hooks/context/get-started-context';
import MissingFields from 'components/page/app-page/MissingFields';
import { generateVacancy } from 'utils/vacancy-generator/vacancyHelpers';
import { ArrowSquareUpRight, Robot, User } from '@phosphor-icons/react';
import { Routes } from 'types/routeTypes';
import { VacancyCopy } from 'types/vacancyTypes';
import { useSymplCookie } from 'hooks/symplCookie';
import { fireEvent } from 'utils/eventHelper';
import CustomUrlForm from '../custom-url/CustomUrlForm';
import CustomUrlPage from '../custom-url/CustomUrlPage';
import { useNavigate } from 'react-router-dom';
import { PostHogFeature } from 'posthog-js/react';
import SalaryEditor from 'components/vacancy/SalaryEditor';
import { SUBSCRIPTION_CAPABILITIES } from 'types/subscriptions/types';
import { validateSalary } from 'utils/salaryHelpers';

export type TEditorModalTypes =
  | 'custom-vacancy-url'
  | 'custom-vacancy-url-no-back'
  | 'first-time'
  | 'ai';

const EditorPage: React.FC = () => {
  const navigate = useNavigate();
  const { setPending, registerAbandon, registerSaveNow, switching } =
    usePendingSave();

  const [usedAiGen, setUsedAiGen] = useSymplCookie('usedAiGen');

  const {
    copyLoading,
    saveHandler,
    copyData,
    copyVacancyId,
    setCopyData,
    generatingFields,
    setGeneratingFields,
    setPayload,
  } = useVacancyContext();

  const {
    activeVacancy,
    setHasUnsavedChanges,
    hasUnsavedChanges,
    brands,
    hasSubscriptionCapability,
    currentVacancy,
    refetchCurrentVacancy,
  } = useNavigationContext();

  const salaryFeatureEnabled = hasSubscriptionCapability(
    SUBSCRIPTION_CAPABILITIES.PAY_TRANSPARENCY_BASE
  );
  const salaryRequired =
    salaryFeatureEnabled && currentVacancy?.brand?.salary_mandatory === true;
  const [salaryValidationErrors, setSalaryValidationErrors] = useState<
    ReturnType<typeof validateSalary>['errors']
  >({});

  const { nextItem, refetchCheckList } = useGetStartedContext();

  const { control, handleSubmit } = useForm();

  const { data: languages } = useApiList('languages');

  const formData = useRef({
    companyDescription: '',
    companyOffers: ['', '', ''],
    toneOfVoice: [''],
  });
  const showedFirstTimeOnce = useRef(false);

  const [showModal, setShowModal] = useState<TEditorModalTypes>();

  const [fetchJobTypes] = useLazyQuery<{ jobTypes: JobType[] }, {}>(
    GET_JOBTYPES_SUBJOBTYPES
  );

  // Kept for the salary card's own error display.
  const validateSalaryBeforeSave = () => {
    if (!salaryRequired || !copyData) {
      setSalaryValidationErrors({});
      return true;
    }

    const rangeEnabled =
      copyData.salary_min !== null &&
      copyData.salary_min !== undefined &&
      copyData.salary_max !== copyData.salary_min;

    const result = validateSalary({
      salary_min: copyData.salary_min,
      salary_max: copyData.salary_max,
      currency: copyData.currency,
      pay_period: copyData.pay_period,
      salary_note: copyData.salary_note,
      rangeEnabled,
      mandatory: true,
    });

    setSalaryValidationErrors(result.errors);
    return Object.keys(result.errors).length === 0;
  };

  /**
   * Auto-save (scenes 1, 3, 5, 12). No Save button on this screen any more.
   *
   * D7: unfinished work is kept. A salary that cannot be stored (max below min)
   * is the one exception: those fields are left out and everything else is
   * still kept, rather than the whole screen refusing to save.
   */
  /*
   * The page content, but ONLY while it is the open campaign's. In the moment
   * after another campaign is picked the content is still the previous one's;
   * auto-save and undo must not see it as this campaign's starting point.
   */
  const ownCopy =
    !!activeVacancy && copyVacancyId === activeVacancy ? copyData : undefined;

  /*
   * D10: the count of the job page this screen is built on. It arrives with
   * the page, and moves with every answer of the server to a save of ours.
   * "Keep mine" sends the save again marked as overwriting.
   */
  // Per campaign: a save handed on after a campaign change must carry the
  // count of the campaign it was typed on, not go without one.
  const builtOn = useRef(new Map<number, number>());
  const arrivedWith = ownCopy?.kept_version ?? null;
  useEffect(() => {
    if (!activeVacancy || arrivedWith === null) return;
    builtOn.current.set(activeVacancy, arrivedWith);
  }, [activeVacancy, arrivedWith]);
  /** "Keep mine": the next save of THIS campaign is marked as overwriting. */
  const overwriteOnce = useRef(false);
  useEffect(() => {
    // Another campaign: a "Keep mine" that never went through does not
    // travel along and overwrite there (review of slice 4).
    overwriteOnce.current = false;
  }, [activeVacancy]);
  const countFor = (campaign: number): BasedOn => ({
    version: builtOn.current.get(campaign) ?? null,
  });

  const storeJobPage = useCallback(
    async (copy: VacancyCopy, signal?: AbortSignal) => {
      // Never silently do nothing: the corner would then say "Saved at" for a
      // request that was never sent (done-point 7).
      if (!activeVacancy) throw new Error('No campaign is open.');

      const { payload, salaryLeftOut } = jobPageRequest(copy);
      const campaign = activeVacancy;
      const basedOn: BasedOn = {
        ...countFor(campaign),
        overwrite: overwriteOnce.current,
      };
      const send = async (body: Record<string, unknown>) => {
        const { keptAt: at, keptVersion } = await saveJobPage(
          campaign,
          body,
          signal,
          basedOn
        );
        overwriteOnce.current = false;
        if (keptVersion !== null) builtOn.current.set(campaign, keptVersion);
        return at;
      };

      try {
        const keptAt = await send(payload);
        return {
          keptAt,
          // 5b item 4, the exact words; and the range sentence of 3 Oct 2026.
          note: salaryLeftOut ? salaryNotSavedNote(copy) : undefined,
        };
      } catch (error) {
        if (!(error instanceof JobPageRefusal)) throw error;

        /*
         * D7: a field that cannot be stored is left out and the REST is kept.
         *
         * The server refuses the whole page over one bad field -- a picture
         * whose file is gone, a salary it cannot read -- and retrying the same
         * request would refuse it again for ever. So the named fields go and
         * everything else on the screen is saved.
         */
        const withoutRefused = { ...payload };
        error.fields.forEach((field) => delete withoutRefused[field]);

        if (Object.keys(withoutRefused).length === 0) throw error;

        const keptAt = await send(withoutRefused);

        return {
          keptAt,
          note:
            salaryLeftOut || error.fields.some((f) => f.startsWith('salary'))
              ? salaryNotSavedNote(copy)
              : undefined,
        };
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeVacancy]
  );

  const {
    state: saveState,
    keptAt,
    note,
    hasPending,
    hasFailed,
    abandon,
    saveNow,
    isKept,
    refusal,
    retryRefused,
  } = useAutoSave<VacancyCopy>({
    value: ownCopy,
    enabled: !!activeVacancy && !copyLoading,
    identity: activeVacancy,
    keptAtOnOpen: ownCopy?.kept_at,
    save: storeJobPage,
    flushOnHide: (copy) => {
      if (!activeVacancy) return;
      sendKeepaliveSave(
        jobPagePath(activeVacancy),
        withCount(jobPageRequest(copy).payload, countFor(activeVacancy))
      );
    },
    // The last resort when the campaign changes under this screen some other
    // way than the picker (which waits for the save): the work goes to the
    // campaign it was typed on, by its own number, never to the open one.
    flushOnIdentityChange: (previousCampaign, copy) => {
      if (typeof previousCampaign !== 'number') return;
      sendKeepaliveSave(
        jobPagePath(previousCampaign),
        withCount(jobPageRequest(copy).payload, countFor(previousCampaign))
      );
    },
  });

  const { canUndo, canRedo, undo, redo } = useUndoRedo<VacancyCopy>({
    value: ownCopy,
    apply: (earlier) => setCopyData(earlier),
    enabled: !!activeVacancy && !copyLoading,
    identity: activeVacancy,
  });

  /*
   * The sidebar asks this before it takes the person to another screen. Only a
   * save that DID NOT GO THROUGH earns the question (D8); work still inside the
   * pause is sent on the way out instead.
   *
   * The clean-up matters: the flag lives at the root of the app, so leaving this
   * screen any other way (the back button, a publish redirect) used to leave it
   * switched on and every link in the app then asked about work that no longer
   * existed.
   */
  useEffect(() => {
    setPending(hasPending && hasFailed);
    registerAbandon(() => {
      // Giving the work up also gives up a "Keep mine" that did not land.
      overwriteOnce.current = false;
      abandon();
    });
    registerSaveNow(saveNow, isKept);
    return () => {
      setPending(false);
      registerAbandon(null);
      registerSaveNow(null);
    };
  }, [
    hasPending,
    hasFailed,
    setPending,
    registerAbandon,
    abandon,
    registerSaveNow,
    saveNow,
    isKept,
  ]);

  /*
   * Both of these used to hang off the Save button finishing. With the button
   * gone they follow a save that landed: the screen stops calling itself
   * unsaved, and the sidebar's publish checklist is asked again so the line
   * about what is still needed cannot go stale (scene 5).
   */
  useEffect(() => {
    if (saveState !== 'saved' || hasPending) return;
    setHasUnsavedChanges(false);
    refetchCheckList();
    // On a campaign whose job is open, what was just kept is not public yet:
    // the sidebar's "changes not yet public" follows it (scene 6).
    window.dispatchEvent(new CustomEvent(WORK_WAS_KEPT));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveState, hasPending]);

  const checkField = (condition: boolean, message: string) =>
    condition ? [message] : [];

  const missingFields = useMemo(() => {
    if (!copyData) return [];

    return [
      ...checkField(!copyData.pictures?.logo, 'No logo set'),
      ...checkField(!copyData.pictures?.image_cb1, 'No first visual set'),
      ...checkField(!copyData.pictures?.image_cb3, 'No second visual set'),
      ...checkField(!copyData.title, 'No title set'),
      ...checkField(
        !copyData.vac_text_essence ||
          isTextEditorEmpty(copyData.vac_text_essence),
        'No essence text set'
      ),
      ...checkField(!copyData.todo_title, 'No todo title set'),
      ...checkField(
        !copyData.vac_text_todo || isTextEditorEmpty(copyData.vac_text_todo),
        'No todo text set'
      ),
      ...checkField(!copyData.expectations_title, 'No expectations title set'),
      ...checkField(
        !copyData.vac_text_expectations ||
          isTextEditorEmpty(copyData.vac_text_expectations),
        'No expectations text set'
      ),
      ...checkField(!copyData.offer_title, 'No offer title set'),
      ...checkField(
        !copyData.vac_text_offer || isTextEditorEmpty(copyData.vac_text_offer),
        'No offer text set'
      ),
      ...checkField(!copyData.company_title, 'No company title set'),
      ...checkField(
        !copyData.vac_text_company ||
          isTextEditorEmpty(copyData.vac_text_company),
        'No company text set'
      ),
    ];
  }, [copyData]);

  const validTargetingData = useMemo(() => {
    const targeting = currentVacancy?.targeting;
    return (
      !!targeting?.vac_name &&
      !!targeting?.brand_id &&
      !!targeting?.locations &&
      !!targeting?.locations.length &&
      !!targeting?.job_type
    );
  }, [currentVacancy?.targeting]);

  useEffect(() => {
    if (
      !currentVacancy?.has_custom_url &&
      !copyData?.vac_url &&
      missingFields.filter((field) => field.includes('text')).length > 2 &&
      validTargetingData &&
      showedFirstTimeOnce.current === false &&
      generatingFields.length === 0
    ) {
      setShowModal('first-time');
      showedFirstTimeOnce.current = true;
    }
  }, [
    missingFields,
    copyData,
    validTargetingData,
    showedFirstTimeOnce,
    generatingFields,
  ]);

  const onSubmit = async ({
    'company-description': companyDescription,
    'company-offers': offers,
    'tone-of-voice': toneOfVoice,
  }: {
    'company-description': string;
    'company-offers': string[];
    'tone-of-voice': string[];
  }) => {
    setShowModal(undefined);
    setGeneratingFields([
      'vac_text_essence',
      'vac_text_uniqueness',
      'vac_text_expectations',
      'vac_text_todo',
      'vac_text_offer',
      'vac_text_company',
    ]);

    // Persist state
    formData.current = {
      companyDescription,
      companyOffers: offers,
      toneOfVoice: toneOfVoice,
    };

    // Fetch data used for AI generation
    const { data: { jobTypes } = {} } = await fetchJobTypes();

    const targeting = currentVacancy?.targeting;

    const jobType = jobTypes?.find(({ id }) => id === targeting?.job_type);

    const jobCategory = targeting?.sub_job_type
      ? jobType?.sub_job_types?.find(({ id }) => id === targeting?.sub_job_type)
          ?.name
      : jobType?.name;

    const payload = {
      jobFunction: targeting?.vac_name!,
      companyName: brands.find(({ id }) => id === targeting?.brand_id)?.name!,
      location: targeting?.locations?.[0].name!,
      jobCategory: jobCategory!,
      companyDescription,
      experienceLevel:
        targeting?.experience
          ?.map((code) => {
            return EXPERIENCE.find(({ code: c }) => c === code)?.name;
          })
          .join(', ') ?? 'None',
      toneOfVoice: toneOfVoice.join(', '),
      offers,
      language:
        languages.find(({ key }) => key === targeting?.language_id)?.label ??
        'English',
    };

    setPayload(payload);

    await generateVacancy({
      vacancyId: activeVacancy!,
      payload,
      setCopyData,
      setGeneratingFields,
    });

    if (!usedAiGen) {
      popConfetti();
      setUsedAiGen(true);
    }

    fireEvent('ai_copy_popup');
  };

  const onCustomLinkSubmit = ({ isModal }: { isModal: boolean }) => {
    if (isModal) setShowModal(undefined);
    refetchCurrentVacancy();
    refetchCheckList();
  };

  // This screen always stores itself: the button stays and goes grey.
  const nextStep = nextStepButton({
    storesItself: true,
    hasPending: hasUnsavedChanges,
    deletionsWaiting: false,
    refused: false,
    hasUnsavedChanges,
  });

  return (
    <AppPage
      heading="Vacancy Page"
      loading={copyLoading}
      disablePadding={true}
      clipboardCopy={
        copyData?.share_url && <CopyToClipboard url={copyData?.share_url} />
      }
      cta={
        <div tw="flex space-x-5">
          {!currentVacancy?.has_custom_url && !!missingFields.length && (
            <MissingFields missingFields={missingFields} />
          )}
          {nextItem?.key === 'typeform' && (
            <Button
              variant="indigo"
              disabled={nextStep.disabled}
              onClick={() => navigate(Routes.INTERVIEW)}
              testId="vacancy-page-next-step-button"
            >
              Go to next step &rarr;
            </Button>
          )}
          <UndoRedo
            canUndo={canUndo}
            canRedo={canRedo}
            onUndo={undo}
            onRedo={redo}
          />
          <SaveStatus
            state={saveState}
            keptAt={keptAt}
            note={note}
            switching={switching}
          />
        </div>
      }
    >
      {/*
        Which campaign's content is on screen, or none while it is still the
        previous campaign's. Nothing reads this but the browser checks: the
        picker shows a picked campaign's name at once, long before its page
        has arrived, and a check that typed in that moment typed into a page
        about to be replaced.
      */}
      <span
        hidden
        data-testid="job-page-content-of"
        data-campaign={ownCopy ? String(activeVacancy) : ''}
      />
      <OutOfDateBox
        refusal={refusal}
        onLoadTheirs={() => {
          // The page is loaded afresh, so the undo steps go with it (D12).
          overwriteOnce.current = false;
          abandon();
          // A moment later: the screen has let go by then, so the browser's
          // own "leave this page?" does not come up for work just given up.
          window.setTimeout(() => window.location.reload(), 100);
        }}
        onKeepMine={() => {
          overwriteOnce.current = true;
          retryRefused();
        }}
      />
      <Modal
        show={showModal === 'first-time'}
        onClose={() => setShowModal(undefined)}
      >
        <Title1>Let's setup your vacancy page!</Title1>
        <div tw="grid grid-cols-2 gap-4 my-8">
          <button
            type="button"
            tw="p-8 ring-2 ring-gray-200 rounded-md grid items-center hover:(bg-blue-50 ring-blue-500) focus:outline-none"
            onClick={() => setShowModal(undefined)}
          >
            <div tw="space-y-4 text-gray-800">
              <User weight="fill" size={32} tw="m-auto" />
              <p tw="font-semibold">I'll create it myself.</p>
            </div>
          </button>
          <button
            type="button"
            tw="p-8 ring-2 ring-gray-200 rounded-md grid items-center hover:(bg-blue-50 ring-blue-500) focus:outline-none"
            onClick={() => setShowModal('ai')}
          >
            <div tw="space-y-4 text-gray-800" data-testid="vacancy-page-ai-gen">
              <Robot weight="fill" size={32} tw="m-auto" />
              <p tw="font-semibold">Create it for me (using AI).</p>
            </div>
          </button>

          <PostHogFeature flag="byov" match={true}>
            <button
              type="button"
              tw="col-span-2 mx-auto w-max p-8 ring-2 ring-gray-200 rounded-md grid items-center hover:(bg-blue-50 ring-blue-500) focus:outline-none"
              onClick={() => setShowModal('custom-vacancy-url')}
            >
              <div tw="space-y-4 text-gray-800">
                <ArrowSquareUpRight weight="fill" size={32} tw="m-auto" />
                <p tw="font-semibold">Use your own vacancy page</p>
              </div>
            </button>
          </PostHogFeature>
        </div>
      </Modal>
      <Modal show={showModal === 'ai'} onClose={() => setShowModal(undefined)}>
        <form tw="py-3 w-auto sm:w-[35rem]" onSubmit={handleSubmit(onSubmit)}>
          <div tw="flex flex-col space-y-5">
            <Controller
              id="company-description"
              name="company-description"
              control={control}
              defaultValue={
                stripHtml(copyData?.vac_text_company ?? '') ||
                formData.current.companyDescription
              }
              rules={{ required: true }}
              render={({ value, onChange }) => (
                <div>
                  <Title3>Describe your company</Title3>
                  <Input
                    id="vacancy-page-company-description"
                    type="text"
                    rows={4}
                    textarea={true}
                    autoGrow={true}
                    value={value}
                    placeholder={
                      'e.g. SME, team of 15 people, founded in 2015, our product is ...'
                    }
                    onChange={(e) => onChange(e.target.value)}
                  />
                </div>
              )}
            />

            <Controller
              id="company-offers"
              name="company-offers"
              control={control}
              defaultValue={
                copyData?.vac_text_uniqueness?.map((value) => value.value) ||
                formData.current.companyOffers
              }
              rules={{ required: true }}
              render={({ value, onChange }) => (
                <div>
                  <Title3>
                    How would you convince people to apply for this job?
                  </Title3>
                  <InputGroup
                    id={'company-offers'}
                    value={value}
                    minInputs={3}
                    maxInputs={5}
                    onChange={onChange}
                  />
                </div>
              )}
            />

            <Controller
              id="tone-of-voice"
              name="tone-of-voice"
              control={control}
              defaultValue={formData.current.toneOfVoice}
              rules={{ required: true }}
              render={({ value, onChange }) => (
                <div>
                  <Title3>Which tone of voice is best suited?</Title3>
                  <PillGroup
                    selected={value}
                    options={[
                      '💼 Professional',
                      '😊 Friendly',
                      '😃 Enthusiastic',
                      '💪 Confident',
                      '📝 Formal',
                      '💬 Conversational',
                      '🗣 Assertive',
                      '😎 Excited',
                    ]}
                    testId="vacancy-page-tone"
                    onChange={(options) =>
                      onChange(
                        options
                          .filter(({ selected }) => selected)
                          ?.map(({ key }) => key)
                      )
                    }
                  />
                </div>
              )}
            />

            <div tw="ml-auto">
              <Button type="submit" testId="vacancy-page-generate">
                Generate 🪄
              </Button>
            </div>
          </div>
        </form>
      </Modal>
      <Modal
        show={
          showModal === 'custom-vacancy-url' ||
          showModal === 'custom-vacancy-url-no-back'
        }
        onClose={() => setShowModal(undefined)}
      >
        <CustomUrlForm
          onSubmit={() => onCustomLinkSubmit({ isModal: true })}
          showWarning
          {...(showModal === 'custom-vacancy-url' && {
            onBack: () => setShowModal('first-time'),
          })}
        />
      </Modal>
      {currentVacancy?.has_custom_url ? (
        <CustomUrlPage
          onSubmit={() => onCustomLinkSubmit({ isModal: false })}
        />
      ) : (
        <div tw="flex h-full min-h-0 flex-col overflow-hidden">
          {salaryFeatureEnabled && (
            <SalaryEditor
              mandatory={salaryRequired}
              validationErrors={salaryValidationErrors}
            />
          )}
          <div tw="min-h-0 flex-1">
            <VacancyPreviewer
              validTargetingData={validTargetingData}
              setShowModal={setShowModal}
            />
          </div>
        </div>
      )}
    </AppPage>
  );
};

export default EditorPage;
